export type AiFollowup = {
  /** Short label shown on the chip. */
  label: string;
  /** Full message sent when the chip is clicked. */
  send: string;
};

/**
 * AI may append suggested next questions as:
 * <!--FOLLOWUPS [ { "label": "...", "send": "..." }, ... ] FOLLOWUPS-->
 * Sometimes wrapped in a markdown code fence (which would leave an empty box
 * if we only stripped the comment).
 */
const FOLLOWUPS_BLOCK_RE =
  /<!--\s*FOLLOWUPS\s*([\s\S]*?)\s*FOLLOWUPS\s*-->/gi;

/** Incomplete block still being streamed (no closing FOLLOWUPS--> yet). */
const FOLLOWUPS_INCOMPLETE_RE = /<!--\s*FOLLOWUPS\b[\s\S]*$/i;

/** Whole fenced block whose body is only a FOLLOWUPS marker (complete or not). */
const FOLLOWUPS_FENCE_RE =
  /```[^\n]*\r?\n\s*<!--\s*FOLLOWUPS\b[\s\S]*?(?:FOLLOWUPS\s*-->)?\s*```/gi;

/** Empty fenced blocks left after stripping FOLLOWUPS from inside a fence. */
const EMPTY_FENCE_RE = /```[^\n]*\r?\n\s*```/g;

function normalizeFollowup(raw: unknown): AiFollowup | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const item = raw as Record<string, unknown>;
  const label = typeof item.label === 'string' ? item.label.trim() : '';
  const send = typeof item.send === 'string' ? item.send.trim() : '';
  if (!label || !send) return null;
  return { label, send };
}

function collectFromPayload(
  payload: string,
  found: AiFollowup[],
  seen: Set<string>
): void {
  let parsed: unknown;
  try {
    parsed = JSON.parse(payload);
  } catch {
    return;
  }
  if (!Array.isArray(parsed)) return;

  for (const entry of parsed) {
    const followup = normalizeFollowup(entry);
    if (!followup) continue;
    const key = `${followup.label}\0${followup.send}`;
    if (seen.has(key)) continue;
    seen.add(key);
    found.push(followup);
  }
}

/** Extract follow-up chips from assistant message text (complete marker only). */
export function parseFollowups(
  content: string | null | undefined
): AiFollowup[] {
  if (!content) return [];

  const found: AiFollowup[] = [];
  const seen = new Set<string>();

  for (const match of content.matchAll(FOLLOWUPS_BLOCK_RE)) {
    const payload = match[1]?.trim();
    if (!payload) continue;
    collectFromPayload(payload, found, seen);
  }

  return found;
}

/** Remove FOLLOWUPS markers and any empty code-fence shell left behind. */
/** `<br>` from any API field is copied into markdown and renders as the text `br>`. */
export function stripHtmlBreaks(text: string): string {
  return text
    .replace(/<br\s*\/?>/gi, " · ")
    .replace(/&lt;br\s*\/?&gt;/gi, " · ")
    .replace(/(^|[\s>|])br>/gi, "$1");
}

export function stripFollowups(
  content: string | null | undefined
): string {
  if (!content) return '';
  return content
    .replace(FOLLOWUPS_FENCE_RE, '')
    .replace(FOLLOWUPS_BLOCK_RE, '')
    .replace(FOLLOWUPS_INCOMPLETE_RE, '')
    .replace(EMPTY_FENCE_RE, '')
    .replace(/\n{3,}/g, '\n\n')
    .trimEnd();
}
